import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { colors, fonts } from 'bench-style-constants';
import { Fill } from 'layout-core-components';
import { Button } from 'primitive-components';
import FloatingActions from 'floating-actions';

const StyledButton = styled(Button)`
  display: flex;
  justify-content: space-between;
`;

const StyledTextField = styled.input.attrs(() => ({
  type: 'text',
}))`
  padding: 8px 16px;
  background: ${colors.shell};
  border: 0;
  border-radius: 0.25rem;
  color: ${colors.charcoal};
  font-family: ${fonts.circular};
  font-size: 16px;
  font-weight: 300;
  line-height: 24px;
  transition: background 0.25s ease-in-out, color 0.25s ease-in-out;
  width: 100%;

  &:focus {
    outline: 0;
    box-shadow: inset 0 0 0 1px ${colors.stream};
  }

  &::placeholder {
    color: ${colors.dust};
  }
`;

const StyledTextFieldWrapper = styled.div`
  &:hover ${StyledTextField}, &:focus-within ${StyledTextField} {
    padding-right: 4.5rem;
  }
`;

export default class AddCategorySegmentButton extends React.Component {
  static propTypes = {
    segmentCategoryName: PropTypes.string.isRequired,
    startOpen: PropTypes.bool.isRequired,
    onSegmentCategoryAdded: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isEditable: this.props.startOpen || false,
      name: '',
    };
    this.handleBlur = this.handleBlur.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleBlur(event) {
    /**
     * Fallback to use Firefox native legacy property `event.explicitOriginalTarget` for
     * cases where `relatedTarget` is set to null.
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Event/Comparison_of_Event_Targets
     * @link https://github.com/facebook/react/issues/2011
     */
    const relatedTarget = event.relatedTarget || event.nativeEvent.explicitOriginalTarget;
    if (event.currentTarget.contains(relatedTarget)) return;
    this.toggleEditMode();
  }

  handleChange(event) {
    this.setState({ name: event.target.value });
  }

  handleSubmit(event) {
    event.preventDefault();
    const { name } = this.state;
    this.props.onSegmentCategoryAdded({ name });
    this.setState({ name: '' });
  }

  handleCancel() {
    this.setState({ name: '' });
    this.toggleEditMode();
  }

  toggleEditMode() {
    this.setState(({ isEditable }) => ({ isEditable: !isEditable }));
  }

  render() {
    if (this.state.isEditable) {
      return (
        <form onSubmit={this.handleSubmit}>
          <StyledTextFieldWrapper onBlur={this.handleBlur}>
            <FloatingActions>
              <Fill width="parent">
                <StyledTextField
                  autoFocus
                  autoComplete="off"
                  maxLength={30}
                  name="name"
                  onChange={this.handleChange}
                  onKeyDown={this.handleKeyDown}
                  placeholder={`Add a ${this.props.segmentCategoryName}`}
                  value={this.state.name}
                />
              </Fill>
              <FloatingActions.List transition={this.state.name} fadeIn={this.state.name} padRight>
                <FloatingActions.Item icon="checkmark" type="submit" />
                <FloatingActions.Item
                  styleType="danger"
                  icon="cancel"
                  onClick={this.handleCancel}
                />
              </FloatingActions.List>
            </FloatingActions>
          </StyledTextFieldWrapper>
        </form>
      );
    }

    return (
      <StyledButton
        onClick={() => this.toggleEditMode()}
        iconRight="plus"
        styleType="primary"
        subtle
      >
        {`Add a ${this.props.segmentCategoryName}`}
      </StyledButton>
    );
  }
}
